// Keep in sync with cui-analytics-service/src/types.ts
export type AnalyticsEventType =
    | 'session_start'
    | 'consent_accepted'
    | 'chat_opened'
    | 'user_message_sent'
    | 'message_feedback'
    | 'link_clicked';

export interface AnalyticsEventPayload {
    sessionId: string;
    type: AnalyticsEventType;
    timestamp: string;
    value?: string;
    messageId?: string;
    pageId?: string;
    host?: string;
    referrer?: string;
    userAgent?: string;
    partnerId?: string;
}

export interface AnalyticsEventResponse {
    success: boolean;
    eventId?: string;
    error?: string;
}

/**
 * Send an analytics event to the backend API
 * Non-blocking: failures are logged but don't throw
 */
export async function sendEvent(
    baseUrl: string,
    payload: AnalyticsEventPayload
): Promise<AnalyticsEventResponse | null> {
    try {
        const response = await fetch(`${baseUrl}/events`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Analytics API error:', {
                status: response.status,
                error: errorData,
            });
            return {
                success: false,
                error: errorData.error || `HTTP ${response.status}`,
            };
        }

        const data: AnalyticsEventResponse = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to send analytics event:', error);
        return null;
    }
}

let analyticsPartnerId: string | undefined;

/**
 * Configure the partnerId auto-filled onto every event payload built via
 * buildEventPayload(). Call with undefined to clear it.
 */
export function setAnalyticsPartnerId(partnerId?: string): void {
    analyticsPartnerId = partnerId;
}

/**
 * Build an event payload with common fields auto-filled
 */
export function buildEventPayload(
    sessionId: string,
    type: AnalyticsEventType,
    extras?: Partial<AnalyticsEventPayload>
): AnalyticsEventPayload {
    const payload: AnalyticsEventPayload = {
        sessionId,
        type,
        timestamp: new Date().toISOString(),
        pageId: typeof window !== 'undefined' ? window.location.pathname : undefined,
        host: typeof window !== 'undefined' ? window.location.host : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        partnerId: analyticsPartnerId,
        ...extras,
    };
    return payload;
}

const SESSION_START_KEY_PREFIX = 'cui_session_start_sent_';

export function wasSessionStartSent(sessionId: string): boolean {
    try {
        return sessionStorage.getItem(`${SESSION_START_KEY_PREFIX}${sessionId}`) === '1';
    } catch {
        return false;
    }
}

export function markSessionStartSent(sessionId: string): void {
    try {
        sessionStorage.setItem(`${SESSION_START_KEY_PREFIX}${sessionId}`, '1');
    } catch {
        // sessionStorage not available
    }
}
